import { WatchContractEventReturnType } from "viem";
import { earnWethGateway, uTokenFactoryContract } from "../contracts";
import {
  ReserveState,
  SupplyTopics,
  WithdrawTopics,
} from "../contracts/UTokenFactoryContract";
import Module from "./Module";
import { Address, OptionsWriteMethod } from "@unlockdfinance/verislabs-web3";
import {
  SupportedChainIds,
  externalWalletModule,
} from "../clients/verisModule";
import { ICurrency, IErc20Currency } from "./types/currency/ICurrency";
import apyModule from "./ApyModule";
import { Erc20Currency } from "./types/currency/Currency";

export type CurrencyGeneralData = {
  id: string;
  name: string;
  label: string;
  tvl: bigint;
  utilizationRate: string;
  apy: number;
  reserveState: ReserveState;
};

class EarnModule extends Module {
  getProtocolLiquidity(
    underlyingAsset: Address,
    chainId: SupportedChainIds
  ): Promise<bigint> {
    return uTokenFactoryContract.totalAvailableSupply(underlyingAsset, chainId);
  }

  async getDepositedBalance(
    currencyAddress: Address,
    walletAddress: Address,
    chainId: SupportedChainIds
  ): Promise<bigint> {
    const tokens = await uTokenFactoryContract.scaledBalanceOf(
      currencyAddress,
      walletAddress,
      chainId
    );

    return tokens;
  }

  async getReserveState(currency: IErc20Currency): Promise<ReserveState> {
    const [, isFrozen, isPaused] = await uTokenFactoryContract.getFlags(
      currency.address,
      currency.chain.id
    );

    if (isPaused) return ReserveState.STOPPED;
    else if (isFrozen) return ReserveState.FREEZED;
    else return ReserveState.ACTIVE;
  }

  async getCurrencyGeneralData(
    currency: IErc20Currency,
    expressedInDollars: boolean = false
  ): Promise<CurrencyGeneralData> {
    const [totalSuppliedInCurrency, scaledTotalDebtMarket, apy, reserveState] =
      await Promise.all([
        this.getTotalSupplied(currency.address, currency.chain.id),
        this.getScaledTotalDebtMarket(currency.address, currency.chain.id),
        apyModule.getDepositApy(currency),
        this.getReserveState(currency),
      ]);

    const tvlInCurrency = totalSuppliedInCurrency + scaledTotalDebtMarket;

    const utilizationRate =
      totalSuppliedInCurrency !== BigInt(0)
        ? (
          (Number(scaledTotalDebtMarket) / Number(totalSuppliedInCurrency)) *
          100
        ).toFixed(2)
        : "0";

    const tvl = expressedInDollars
      ? await currency.parseToDollar(tvlInCurrency)
      : tvlInCurrency;

    return {
      id: currency.id,
      name: currency.name,
      tvl,
      utilizationRate,
      reserveState,
      label: currency.label,
      apy,
    };
  }

  private getTotalSupplied(
    currencyAddress: Address,
    chainId: SupportedChainIds
  ): Promise<bigint> {
    return uTokenFactoryContract.totalSupply(currencyAddress, chainId);
  }

  private getScaledTotalDebtMarket(
    currencyAddress: Address,
    chainId: SupportedChainIds
  ): Promise<bigint> {
    return uTokenFactoryContract.getScaledTotalDebtMarket(
      currencyAddress,
      chainId
    );
  }

  async deposit(
    currency: ICurrency,
    amount: bigint,
    options?: OptionsWriteMethod
  ): Promise<Address | null> {
    const walletAddress = externalWalletModule.address!;

    if (currency.name === "weth") {
      const output = await earnWethGateway.depositETH(
        walletAddress,
        amount,
        options
      );

      return output.hash;
    } else {
      const output = await uTokenFactoryContract.deposit(
        currency.address!,
        amount,
        walletAddress,
        options
      );

      return output.hash;
    }
  }

  async getErc20ToApprove(
    underlyingAsset: Address,
    chainId: SupportedChainIds
  ): Promise<IErc20Currency> {
    const address = await uTokenFactoryContract.getScaledToken(underlyingAsset, chainId);

    // TODO: improve this
    return new Erc20Currency({
      address,
      decimals: 18,
      chainId,
      chainlinkAddress: "0x",
      decimalsToShow: 2,
      label: "",
      name: "",
      srcPrimary: "",
      srcSecondary: "",
      isWrappedNative: false,
    })
  }

  onDeposit(
    onLogs: (depositTopics: SupplyTopics) => void,
    chainId: SupportedChainIds
  ) {
    return uTokenFactoryContract.onDeposit(
      externalWalletModule.address!,
      onLogs,
      chainId
    );
  }

  async withdraw(
    currency: ICurrency,
    amount: bigint,
    totalWithdraw?: boolean,
    options?: OptionsWriteMethod
  ): Promise<Address | null> {
    let _amount = amount;

    if (totalWithdraw) {
      _amount = await this.getDepositedBalance(
        currency.address!,
        externalWalletModule.address!,
        currency.chain.id,
      );
    }

    const walletAddress = externalWalletModule.address!;

    if (currency.name === "weth") {
      const output = await earnWethGateway.withdrawETH(
        _amount,
        walletAddress,
        options
      );

      return output.hash;
    } else {
      const output = await uTokenFactoryContract.withdraw(
        currency.address!,
        _amount,
        walletAddress,
        options
      );

      return output.hash;
    }
  }

  onWithdraw(
    onLogs: (log: WithdrawTopics) => void,
    chainId: SupportedChainIds
  ) {
    return uTokenFactoryContract.onWithdraw(
      externalWalletModule.address!,
      onLogs,
      chainId
    );
  }

  async onEvents({
    onWithdraw,
    onDeposit,
    chainId,
  }: {
    onWithdraw?: (withdrawTopics: WithdrawTopics) => void;
    onDeposit?: (depositTopics: SupplyTopics) => void;
    chainId: SupportedChainIds;
  }) {
    this.stopEventListeners();

    let unwatchWithdraw: WatchContractEventReturnType | null = null;
    let unwatchDeposit: WatchContractEventReturnType | null = null;

    if (onDeposit) unwatchDeposit = await this.onDeposit(onDeposit, chainId);
    if (onWithdraw)
      unwatchWithdraw = await this.onWithdraw(onWithdraw, chainId);

    this.addEventListenerStopper(() => {
      unwatchWithdraw?.();
      unwatchDeposit?.();
    });
  }
}

export default new EarnModule();
